import React, { useCallback, useMemo } from 'react'
import styled from '@emotion/styled'
import { Icon_add, Icon_remove } from '@yanolja-rnd/ya-com-system-icon'
import { Typography } from '@yanolja-rnd/ya-com-core'

const StepValue = styled.span<{ disabled: boolean }>`
  ${Typography('body1').bold};
  color: ${({ disabled, theme }) => (disabled ? theme.color.gray400 : theme.color.gray900)};
  padding-top: 0.2rem;
`

const Container = styled.label`
  display: grid;
  width: 7.2rem;
  height: 3.2rem;
  gap: 0.2rem;
  border-radius: 0.6rem;
  border: 0.1rem solid ${({ theme }) => theme.color.gray400};
  grid-template-columns: repeat(3, 1fr);
  place-items: center;
`

const IconWrapper = styled.span<{ disabled: boolean }>`
  display: flex;
  &:hover {
    @media (hover: hover) and (pointer: fine) {
      cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
    }
  }

  svg {
    path {
      fill: ${({ disabled, theme }) => (disabled ? theme.color.gray400 : theme.color.gray900)};
    }
  }
`

interface StepperProps {
  value?: number
  min?: number
  max?: number
  step?: number
  disabled?: boolean
  onChange?: (value: number) => void
  disabledMinClick?: (value: number) => void
  disabledMaxClick?: (value: number) => void
}

const Stepper: React.FC<StepperProps> = ({
  value = 0,
  min = 0,
  max = 10,
  step = 1,
  disabled = false,
  disabledMinClick,
  disabledMaxClick,
  onChange,
}) => {
  const disabledDecrement = useMemo(() => value <= min || disabled, [value, min, disabled])
  const disabledIncrement = useMemo(() => value >= max || disabled, [value, max, disabled])

  const onDecrement = useCallback(() => {
    const v = Math.max(value - step, min)
    onChange?.(v)
  }, [value, min, step, onChange])
  const onIncrement = useCallback(() => {
    const v = Math.min(value + step, max)
    onChange?.(v)
  }, [value, max, step, onChange])
  const onDisabledMinClick = useCallback(() => {
    disabledMinClick?.(value)
  }, [value, min, step, disabledMinClick])
  const onDisabledMaxClick = useCallback(() => {
    disabledMaxClick?.(value)
  }, [value, max, step, disabledMaxClick])

  const handleClickDecrement = () => {
    if (disabledDecrement) {
      onDisabledMinClick()
    } else {
      onDecrement()
    }
  }

  const handleClickIncrement = () => {
    if (disabledIncrement) {
      onDisabledMaxClick()
    } else {
      onIncrement()
    }
  }

  return (
    <Container>
      <IconWrapper disabled={disabledDecrement} onClick={handleClickDecrement}>
        <Icon_remove size={1.2} unit="rem" />
      </IconWrapper>
      <StepValue disabled={disabled}>{value}</StepValue>
      <IconWrapper disabled={disabledIncrement} onClick={handleClickIncrement}>
        <Icon_add size={1.2} unit="rem" />
      </IconWrapper>
    </Container>
  )
}

export default Stepper
